import { useEffect, useState, useCallback, useRef } from "react";
import { Form, Modal, OverlayTrigger, Popover } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useRouteLoaderData } from "react-router-dom";
import { Alphabet, At, Calendar3, InfoCircleFill, Plus, Telephone, X } from "react-bootstrap-icons";
import { LocalDate } from "@js-joda/core";
import { Button } from "./Controls/Button";
import { HeaderText } from "./Controls/HeaderText";
import { Input } from "./Controls/Input";
import { PhoneInput } from "./Controls/PhoneInput";
import { Api } from "./Data/Api";
import { Facility, FacilityInviteCredentialCapability } from "./Data/ApiTypes";
import { FEATURES, isFeatureEnabled } from "./Data/Features";
import { cents, showMoney } from "./Data/Money";
import { showBrowserDate, showDate } from "./Data/Date";
import { ServerRequestsSubscriptionGroupContractRuleDto } from "./shared/api/axios-client";
import "./GroupParkerAddModal.css";

type AssignGroupOption = {
    id: number;
    name: string;
    contractRules?: ServerRequestsSubscriptionGroupContractRuleDto[] | null;
};

export interface GroupParkerPreviewResult {
    price: number;
    subtotal: number;
    parkerFees: number;
    isCurrentPeriod: boolean;
    billingStartsOn?: string | null;
    willAmendOpenInvoice?: boolean;
}

export interface GroupParkerFeeLine {
    description: string;
    amountCents: number;
    cadence: 0 | 1; // OneTime | Recurring
}

export interface PendingGroupParker {
    key: string;
    name: string;
    emailAddress: string;
    phoneNumber: string;
    startDate: string; // yyyy-MM-dd
    previewPrice: number;
    previewSubtotal?: number;
    previewParkerFees?: number;
    isCurrentPeriod: boolean;
    billingStartsOn?: string | null;
    isExtraSlot: boolean;
    subscriptionGroupId?: number | null;
    subscriptionGroupName?: string | null;
    fees?: GroupParkerFeeLine[];
    pendingIntegrationCredential?: string | null;
}

interface FormValues {
    name: string;
    emailAddress: string;
    phoneNumber: string;
    startDate: string;
    subscriptionGroupId: string;
    pendingIntegrationCredential: string | null;
}

interface Props {
    show: boolean;
    onHide: () => void;
    facilityId: number;
    subscriptionId: number;
    subscriptionStart: LocalDate;
    pendingAssignCount: number;
    /** Cart rows not yet finalized — used for same-group contract tier preview. */
    pendingParkers?: Pick<PendingGroupParker, "subscriptionGroupId">[];
    fillsExtraSlot: boolean;
    canAssignAnother: boolean;
    isAccountsReceivable?: boolean;
    /** Enhanced groups: optional group picker. */
    isEnhancedGroup?: boolean;
    groups?: AssignGroupOption[];
    defaultSubscriptionGroupId?: number | null;
    /** NeedsSetup: no price preview / billing helper — parkers created without checkout. */
    skipBillingPreview?: boolean;
    /** Plan monthly unit price in cents — for contract rule full-price display. */
    basePriceCents?: number;
    onAddToCart: (pending: PendingGroupParker, options?: { assignAnother?: boolean }) => void | Promise<void>;
}

/** Assign form → add to cart only (no persist / no pay). */
export function GroupParkerAddModal({
    show,
    onHide,
    facilityId,
    subscriptionId,
    subscriptionStart,
    pendingAssignCount,
    pendingParkers = [],
    fillsExtraSlot,
    canAssignAnother,
    isAccountsReceivable = false,
    isEnhancedGroup = false,
    groups = [],
    defaultSubscriptionGroupId,
    skipBillingPreview = false,
    basePriceCents = 0,
    onAddToCart,
}: Props) {
    const facility = useRouteLoaderData("facility") as Facility;
    const subscriptionFeesEnabled = isFeatureEnabled(facility, FEATURES.SUBSCRIPTION_FEES);
    const today = showBrowserDate(LocalDate.now());
    const subStartStr = showBrowserDate(subscriptionStart);
    const minDate = subStartStr > today ? subStartStr : today;

    const [credentialCapability, setCredentialCapability] = useState<FacilityInviteCredentialCapability>({
        hasCredentialUpdateIntegration: false,
        rfidValidationPattern: null,
        rfidValidationMaxLength: null,
    });
    const showCredentialField = credentialCapability.hasCredentialUpdateIntegration;
    const maxCredentialLength = credentialCapability.rfidValidationMaxLength ?? 20;

    const { register, handleSubmit, watch, reset, setFocus, setValue, formState: { errors, isSubmitting } } = useForm<FormValues>({
        defaultValues: {
            name: "",
            emailAddress: "",
            phoneNumber: "",
            startDate: minDate,
            subscriptionGroupId: defaultSubscriptionGroupId ? String(defaultSubscriptionGroupId) : "",
            pendingIntegrationCredential: null,
        },
    });
    const nameInputRef = useRef<HTMLInputElement | null>(null);
    const nameRegister = register("name", { required: "Name is required" });

    useEffect(() => {
        if (!show) return;
        let ignore = false;
        Api.facilityParkerInviteCredentialCapability(facilityId).then(res => {
            if (!ignore && res.isOk) setCredentialCapability(res.value);
        });
        return () => { ignore = true; };
    }, [show, facilityId]);

    useEffect(() => {
        if (!showCredentialField) {
            setValue("pendingIntegrationCredential", null);
        }
    }, [showCredentialField, setValue]);

    const focusName = useCallback(() => {
        requestAnimationFrame(() => {
            setFocus("name");
            nameInputRef.current?.focus();
            nameInputRef.current?.select();
        });
    }, [setFocus]);

    const [preview, setPreview] = useState<GroupParkerPreviewResult | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [feeDesc, setFeeDesc] = useState("");
    const [feeAmount, setFeeAmount] = useState("");
    // Release: one-time only; restore when monthly cadence dropdown is re-enabled.
    // const [feeCadence, setFeeCadence] = useState<"0" | "1">("0");
    const [fees, setFees] = useState<GroupParkerFeeLine[]>([]);
    const [showFeeCompose, setShowFeeCompose] = useState(false);
    const feeDescInputRef = useRef<HTMLInputElement | null>(null);

    const watchedStart = watch("startDate");
    const watchedGroupId = watch("subscriptionGroupId");
    const selectedGroupId = watchedGroupId ? Number(watchedGroupId) : null;
    const selectedGroup = selectedGroupId
        ? groups.find(g => g.id === selectedGroupId)
        : undefined;
    const selectedContractRules = (selectedGroup?.contractRules ?? []).filter(r => r != null);
    const pendingSameGroupAssignCount = selectedGroupId
        ? pendingParkers.filter(p => p.subscriptionGroupId === selectedGroupId).length
        : 0;

    useEffect(() => {
        if (!show || !watchedStart || skipBillingPreview) {
            if (skipBillingPreview) {
                setPreview(null);
                setPreviewLoading(false);
            }
            return;
        }
        let cancelled = false;
        setPreviewLoading(true);
        Api.groupParkerPreview(facilityId, subscriptionId, {
            startDate: watchedStart,
            pendingAssignCount,
            pendingSameGroupAssignCount,
            subscriptionGroupId: selectedGroupId,
        })
            .then(r => {
                if (!cancelled) setPreview(r.isOk ? r.value : null);
            })
            .catch(() => { if (!cancelled) setPreview(null); })
            .finally(() => { if (!cancelled) setPreviewLoading(false); });
        return () => { cancelled = true; };
    }, [watchedStart, watchedGroupId, show, facilityId, subscriptionId, pendingAssignCount, pendingSameGroupAssignCount, selectedGroupId, skipBillingPreview]);

    useEffect(() => {
        if (show) {
            reset({
                name: "",
                emailAddress: "",
                phoneNumber: "",
                startDate: minDate,
                subscriptionGroupId: defaultSubscriptionGroupId ? String(defaultSubscriptionGroupId) : "",
                pendingIntegrationCredential: null,
            });
            setPreview(null);
            setFees([]);
            setFeeDesc("");
            setFeeAmount("");
            // setFeeCadence("0");
            setShowFeeCompose(false);
        }
    }, [show, minDate, reset, defaultSubscriptionGroupId]);

    useEffect(() => {
        if (!showFeeCompose) return;
        requestAnimationFrame(() => feeDescInputRef.current?.focus());
    }, [showFeeCompose]);

    useEffect(() => {
        if (!show) return;
        const timer = window.setTimeout(focusName, 50);
        return () => window.clearTimeout(timer);
    }, [show, focusName]);

    const handleClose = () => {
        reset();
        setPreview(null);
        onHide();
    };

    const addToCart = async (data: FormValues, assignAnother: boolean) => {
        if (!skipBillingPreview && !preview) return;
        const billingOn = skipBillingPreview ? null : (preview?.billingStartsOn ?? null);
        const groupId = data.subscriptionGroupId ? Number(data.subscriptionGroupId) : null;
        const groupName = groupId
            ? groups.find(g => g.id === groupId)?.name ?? null
            : null;
        const cartFees = subscriptionFeesEnabled ? fees : [];
        const goodsCents = cartFees.reduce((s, f) => s + f.amountCents, 0);
        const parkingSubtotal = skipBillingPreview ? 0 : (preview?.subtotal ?? 0);
        await onAddToCart({
            key: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            name: data.name.trim(),
            emailAddress: data.emailAddress.trim(),
            phoneNumber: data.phoneNumber.trim(),
            startDate: data.startDate,
            previewPrice: parkingSubtotal + goodsCents,
            previewSubtotal: parkingSubtotal + goodsCents,
            previewParkerFees: 0,
            isCurrentPeriod: skipBillingPreview ? true : (preview?.isCurrentPeriod ?? false),
            billingStartsOn: billingOn,
            isExtraSlot: fillsExtraSlot,
            subscriptionGroupId: groupId,
            subscriptionGroupName: groupName,
            fees: cartFees.length > 0 ? cartFees : undefined,
            pendingIntegrationCredential: showCredentialField
                ? (data.pendingIntegrationCredential ?? null)
                : null,
        }, { assignAnother });

        if (assignAnother) {
            reset({
                name: "",
                emailAddress: "",
                phoneNumber: "",
                startDate: data.startDate,
                subscriptionGroupId: data.subscriptionGroupId,
                pendingIntegrationCredential: null,
            });
            setPreview(null);
            setFees([]);
            setFeeDesc("");
            setFeeAmount("");
            setShowFeeCompose(false);
            focusName();
            return;
        }
        handleClose();
    };

    const submitDisabled = skipBillingPreview
        ? isSubmitting
        : !preview || previewLoading || isSubmitting;

    return (
        <Modal show={show} onHide={handleClose} centered fullscreen="sm-down" onEntered={focusName}>
            <Modal.Header closeButton className="border-0 pb-0 px-3 pt-3">
                <HeaderText>Assign Parker</HeaderText>
            </Modal.Header>
            <Modal.Body className="pt-2 px-3">
                <Form onSubmit={e => e.preventDefault()}>
                    <Input
                        type="text"
                        icon={<Alphabet />}
                        label="Name"
                        placeholder="Full name"
                        {...nameRegister}
                        ref={el => {
                            nameRegister.ref(el);
                            nameInputRef.current = el as HTMLInputElement | null;
                        }}
                        error={errors.name}
                    />
                    <Input
                        type="email"
                        icon={<At />}
                        label="Email address (optional)"
                        placeholder="parker@example.com"
                        {...register("emailAddress", {
                            validate: value => {
                                const trimmed = value.trim();
                                if (!trimmed) return true;
                                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed) || "Enter a valid email";
                            },
                        })}
                        error={errors.emailAddress}
                    />
                    <PhoneInput
                        icon={<Telephone />}
                        label="Phone Number (Optional)"
                        placeholder="555-555-5555"
                        value={watch("phoneNumber") ?? ""}
                        {...register("phoneNumber", {
                            pattern: {
                                value: /^$|^\d{3}-\d{3}-\d{4}$/,
                                message: "Phone number must be in format ###-###-####",
                            },
                        })}
                        error={errors.phoneNumber}
                    />
                    <Input
                        type="date"
                        icon={<Calendar3 />}
                        label="Start date"
                        min={minDate}
                        {...register("startDate", { required: "Start date is required" })}
                        error={errors.startDate}
                    />
                    {showCredentialField && (
                        <Form.Group className="mb-3">
                            <Form.Label className="mb-1">
                                RFID Number (Optional){" "}
                                <OverlayTrigger
                                    placement="top"
                                    container={document.body}
                                    popperConfig={{
                                        strategy: "fixed",
                                        modifiers: [
                                            {
                                                name: "preventOverflow",
                                                options: {
                                                    padding: 8,
                                                    boundary: "viewport",
                                                },
                                            },
                                            {
                                                name: "flip",
                                                options: {
                                                    fallbackPlacements: ["bottom", "left", "right"],
                                                },
                                            },
                                        ],
                                    }}
                                    overlay={
                                        <Popover id="assign-parker-rfid-popover" style={{ pointerEvents: "none", maxWidth: 280 }}>
                                            <Popover.Header as="h4">RFID Number</Popover.Header>
                                            <Popover.Body>
                                                Assign a physical RFID number to override the auto-generated credential when this parker is registered with the facility integration.
                                            </Popover.Body>
                                        </Popover>
                                    }
                                >
                                    <span className="d-inline-flex align-items-center" style={{ cursor: "help", marginLeft: "4px", width: "16px", height: "16px" }}>
                                        <InfoCircleFill size={14} />
                                    </span>
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                maxLength={maxCredentialLength}
                                {...register("pendingIntegrationCredential", {
                                    maxLength: {
                                        value: maxCredentialLength,
                                        message: `RFID number must be ${maxCredentialLength} characters or fewer`,
                                    },
                                    setValueAs: (value: string | null) => {
                                        const trimmed = value?.trim();
                                        return trimmed ? trimmed : null;
                                    },
                                })}
                            />
                            {errors.pendingIntegrationCredential && (
                                <Form.Text className="d-block text-danger ms-1">
                                    {errors.pendingIntegrationCredential.message}
                                </Form.Text>
                            )}
                        </Form.Group>
                    )}
                    {isEnhancedGroup && groups.length > 0 && (
                        <Form.Group className="mb-3">
                            <Form.Label className="small text-muted">Group (optional)</Form.Label>
                            <Form.Select {...register("subscriptionGroupId")}>
                                <option value="">Unassigned</option>
                                {groups.map(g => (
                                    <option key={g.id} value={g.id}>{g.name}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}
                    {subscriptionFeesEnabled && (
                    <div className="group-parker-fees">
                        <div className="group-parker-fees-title">Optional fee (keycard, etc.)</div>
                        <p className="group-parker-fees-help">
                            Added fees show up here and roll into the estimate below.
                        </p>
                        {fees.length > 0 && (
                            <ul className="group-parker-fees-list">
                                {fees.map((f, i) => (
                                    <li key={i} className="group-parker-fee-chip">
                                        <span className="group-parker-fee-chip-name">{f.description}</span>
                                        <span className="group-parker-fee-chip-badge">
                                            One-time
                                            {/* {f.cadence === 1 ? "Monthly" : "One-time"} */}
                                        </span>
                                        <span className="group-parker-fee-chip-amount">
                                            {showMoney(cents(f.amountCents))}
                                        </span>
                                        <button
                                            type="button"
                                            className="group-parker-fee-chip-remove"
                                            aria-label={`Remove ${f.description}`}
                                            onClick={() => setFees(prev => prev.filter((_, j) => j !== i))}
                                        >
                                            <X size={16} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {showFeeCompose ? (
                            <div className="group-parker-fee-compose">
                                <Form.Control
                                    className="group-parker-fee-compose-desc"
                                    placeholder="Description"
                                    value={feeDesc}
                                    ref={(el: HTMLInputElement | null) => { feeDescInputRef.current = el; }}
                                    onChange={e => setFeeDesc(e.target.value)}
                                />
                                <div className="group-parker-fee-compose-amount">
                                    <span className="group-parker-fee-compose-prefix" aria-hidden>$</span>
                                    <Form.Control
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="0.00"
                                        value={feeAmount}
                                        onChange={e => {
                                            const raw = e.target.value;
                                            if (raw !== "" && !/^\d*\.?\d{0,2}$/.test(raw))
                                                return;
                                            setFeeAmount(raw);
                                        }}
                                    />
                                </div>
                                {/* Billing cadence: one-time only for this release; restore dropdown for multi-select later.
                                <Form.Select
                                    className="group-parker-fee-compose-billing"
                                    value={feeCadence}
                                    onChange={e => setFeeCadence(e.target.value as "0" | "1")}
                                >
                                    <option value="0">One-time</option>
                                    <option value="1">Monthly</option>
                                </Form.Select>
                                */}
                                <Button
                                    type="button"
                                    className="btn-main group-parker-fee-compose-add"
                                    aria-label="Add fee"
                                    onClick={() => {
                                        const dollars = parseFloat(feeAmount);
                                        const amountCents = Number.isFinite(dollars) ? Math.round(dollars * 100) : 0;
                                        if (!feeDesc.trim() || amountCents <= 0) return;
                                        setFees(prev => [...prev, {
                                            description: feeDesc.trim(),
                                            amountCents,
                                            // Release: one-time only; restore feeCadence when monthly is re-enabled.
                                            cadence: 0,
                                        }]);
                                        setFeeDesc("");
                                        setFeeAmount("");
                                        requestAnimationFrame(() => feeDescInputRef.current?.focus());
                                    }}
                                >
                                    <Plus size={16} className="me-1" />
                                    Add
                                </Button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className="group-parker-fee-add-btn"
                                onClick={() => setShowFeeCompose(true)}
                            >
                                + Add a fee
                            </button>
                        )}
                    </div>
                    )}
                    {!skipBillingPreview && (
                        <PreviewSummary
                            preview={preview}
                            loading={previewLoading}
                            startDate={watchedStart}
                            groupName={selectedGroup?.name}
                            contractRules={selectedContractRules}
                            isAccountsReceivable={!!isAccountsReceivable}
                            basePriceCents={basePriceCents}
                            goodsFeesCents={subscriptionFeesEnabled ? fees.reduce((s, f) => s + f.amountCents, 0) : 0}
                        />
                    )}
                    <div className="d-grid d-sm-flex gap-2 mt-3 justify-content-sm-end">
                        {canAssignAnother && (
                                <Button
                                    type="button"
                                    className="px-4"
                                    disabled={submitDisabled}
                                    onClick={handleSubmit(data => addToCart(data, true))}
                                >
                                    Confirm And Add Another
                                </Button>
                            )}
                        <Button
                            type="button"
                            className="btn-main px-4"
                            disabled={submitDisabled}
                            loading={isSubmitting}
                            onClick={handleSubmit(data => addToCart(data, false))}
                        >
                            Confirm
                        </Button>

                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
}

function formatContractRuleLine(
    r: ServerRequestsSubscriptionGroupContractRuleDto,
    basePriceCents: number,
): string {
    const range = `${r.min ?? 0}–${r.max ?? "∞"}`;
    if (!r.price || r.price === 0) return `${range}: Free`;
    const amount = Math.abs(r.price);
    const fullCents = r.type === 1
        ? basePriceCents + amount
        : Math.max(basePriceCents - amount, 0);
    if (fullCents === 0) return `${range}: Free`;
    return `${range}: ${showMoney(cents(fullCents))}/mo`;
}

function parsePreviewDate(value?: string | null): LocalDate | null {
    if (!value) return null;
    try {
        return LocalDate.parse(value.slice(0, 10));
    } catch {
        return null;
    }
}

function deferredStartCopy(
    isAccountsReceivable: boolean,
    startDate: string | undefined,
    billingStartsOn?: string | null,
    noChargeLabel = "No charge today",
): string {
    const start = parsePreviewDate(startDate);
    const nextPeriod = parsePreviewDate(billingStartsOn);
    const beforeNext = start != null && nextPeriod != null && start.isBefore(nextPeriod);
    const periodLabel = nextPeriod != null ? showDate(nextPeriod) : null;
    const when = beforeNext
        ? (periodLabel ? `Starts before the next billing period (${periodLabel})` : "Starts before the next billing period")
        : (periodLabel ? `Starts in a future billing period (${periodLabel})` : "Starts in a future billing period");
    const rest = isAccountsReceivable
        ? "Will be included on an amended or future invoice."
        : `${noChargeLabel}.`;
    return `${when}. ${rest}`;
}

function PreviewSummary({
    preview,
    loading,
    startDate,
    groupName,
    contractRules = [],
    isAccountsReceivable = false,
    basePriceCents = 0,
    goodsFeesCents = 0,
}: {
    preview: GroupParkerPreviewResult | null;
    loading: boolean;
    startDate?: string;
    groupName?: string;
    contractRules?: ServerRequestsSubscriptionGroupContractRuleDto[];
    isAccountsReceivable?: boolean;
    basePriceCents?: number;
    goodsFeesCents?: number;
}) {
    if (loading) {
        return (
            <div
                className="alert alert-muted-gold mb-0 small placeholder-glow"
                role="status"
                aria-label="Calculating cost"
            >
                <div className="d-flex align-items-start justify-content-between gap-2">
                    <div className="d-flex align-items-start gap-2 flex-grow-1 min-w-0">
                        <span
                            className="placeholder rounded-circle flex-shrink-0"
                            style={{ width: 16, height: 16, marginTop: 2 }}
                        />
                        <div className="flex-grow-1 min-w-0">
                            <span className="placeholder col-5 rounded d-block" style={{ height: 14 }} />
                            <span className="placeholder col-10 rounded d-block mt-2" style={{ height: 11 }} />
                            <span className="placeholder col-7 rounded d-block mt-1" style={{ height: 11 }} />
                        </div>
                    </div>
                    <span className="placeholder rounded flex-shrink-0" style={{ width: 52, height: 14 }} />
                </div>
            </div>
        );
    }
    if (!preview) return null;

    const hasContract = contractRules.length > 0;
    const sortedRules = [...contractRules].sort(
        (a, b) => (a.min ?? 0) - (b.min ?? 0),
    );

    if (!preview.isCurrentPeriod && (preview.subtotal ?? 0) === 0) {
        return (
            <div className="alert alert-muted-gold d-flex align-items-start gap-2 mb-0 small" role="alert">
                <InfoCircleFill className="flex-shrink-0 mt-1" />
                <span>
                    {deferredStartCopy(
                        !!isAccountsReceivable,
                        startDate,
                        preview.billingStartsOn,
                    )}
                </span>
            </div>
        );
    }

    const estimatedCents = (preview.subtotal ?? 0) + goodsFeesCents;

    if ((preview.subtotal ?? 0) === 0 && goodsFeesCents === 0) {
        return (
            <div className="alert alert-muted-gold d-flex flex-column gap-2 mb-0 small" role="alert">
                <div className="d-flex align-items-start gap-2">
                    <InfoCircleFill className="flex-shrink-0 mt-1" />
                    <span>Assigning to an open spot. No charge today.</span>
                </div>
                {hasContract && groupName && (
                    <div className="ps-4">
                        <div className="fw-semibold mb-1">{groupName} contract</div>
                        {sortedRules.map((r, i) => (
                            <div key={i} className="text-muted" style={{ fontSize: "0.8rem", lineHeight: 1.35 }}>
                                {formatContractRuleLine(r, basePriceCents)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="alert alert-muted-gold d-flex align-items-start justify-content-between gap-2 mb-0 small" role="alert">
            <div className="d-flex align-items-start gap-2 min-w-0">
                <InfoCircleFill className="flex-shrink-0 mt-1" />
                <div className="min-w-0">
                    <div className="fw-semibold">Estimated price</div>
                    {hasContract && groupName ? (
                        <div className="mt-1">
                            <div className="text-muted" style={{ fontSize: "0.8rem", lineHeight: 1.3 }}>
                                Includes <strong>{groupName}</strong> contract pricing (prorated from start date).
                            </div>
                            {sortedRules.map((r, i) => (
                                <div key={i} className="text-muted" style={{ fontSize: "0.8rem", lineHeight: 1.35 }}>
                                    {formatContractRuleLine(r, basePriceCents)}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-muted" style={{ fontSize: "0.8rem", lineHeight: 1.3, marginTop: 2 }}>
                            Prices are based on start date and the prorating rules for full and partial months.
                        </div>
                    )}
                </div>
            </div>
            <span className="fw-semibold flex-shrink-0">{showMoney(cents(estimatedCents))}</span>
        </div>
    );
}
